import React, { useState, useEffect } from 'react';
import {
  Button,
  Form,
  Input,
  Select,
  Radio,
  DatePicker,
  Spin,
  message,
} from 'antd';
import moment from 'moment';

import { updateItem, readItems, readUsers } from '@directus/sdk';
import { getDirectusClient } from '../../../lib/directus';
import { useSelector } from 'react-redux';

const { TextArea } = Input;

const EditCircuitoForm = ({ circuito, onSuccess, onCancel }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [vendedores, setVendedores] = useState([]);
  const [dias, setDias] = useState([]);
  const [loadingVendedores, setLoadingVendedores] = useState(false);
  const [tipo, setTipo] = useState(circuito?.tipo || 'dias');
  const [fielderror, setFielderror] = useState('');
  const [error, setError] = useState(false);
  const [savedDate, setSavedDate] = useState(circuito?.fecha_inicio);
  const userBodega = useSelector(state => state.userdata.bodega?.id);
  const userRoleID = useSelector(state => state.userdata.role?.name);
  // Load vendedores data
  useEffect(() => {
    getVendedores();
    getDias();
    setSavedDate(circuito?.fecha_inicio);
    // Set initial form values with proper date handling
    form.setFieldsValue({
      nombre: circuito?.nombre,
      descripcion: circuito?.descripcion,
      vendedor: circuito?.vendedor,
      tipo: circuito?.tipo || 'dias',
      dias:
        circuito?.tipo === 'dias'
          ? circuito?.dias?.map(d => d.dias_id.id) || []
          : undefined,
      tiempo: circuito?.tipo === 'tiempo' ? circuito?.tiempo : undefined,
      dia_tiempo:
        circuito?.tipo === 'tiempo' ? circuito?.dia_tiempo : undefined,
    });

    setTipo(circuito?.tipo || 'dias');
  }, [circuito]);

  const getVendedores = async () => {
    setLoadingVendedores(true);
    const directus = await getDirectusClient();

    try {
      const result = await directus.request(
        readUsers({
          fields: ['first_name', 'id', 'last_name'],
          filter: {
            role: {
              name: {
                _eq: 'vendedor rutas',
              },
            },
            ...(userRoleID !== 'adminapp' && userRoleID !== 'Administrator'
              ? { bodega: { id: { _eq: userBodega } } }
              : {}),
          },
        })
      );
      setVendedores(result);
    } catch (err) {
      console.error('Error loading vendedores:', err);
      message.error('No se pudieron cargar los vendedores');
    } finally {
      setLoadingVendedores(false);
    }
  };

  const getDias = async () => {
    setLoading(true);
    const directus = await getDirectusClient();

    try {
      const result = await directus.request(readItems('dias', {}));
      setDias(result);
    } catch (err) {
      console.error('Error loading dias:', err);
      message.error('No se pudieron cargar los días');
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async values => {
    setLoading(true);
    setError(false);
    setFielderror('');

    const directus = await getDirectusClient();

    let diasUpdate = [];
    if (values.tipo === 'dias' && values.dias) {
      diasUpdate = values.dias.map(dia => ({
        circuitos_id: '+',
        dias_id: {
          id: dia,
        },
      }));
    }

    try {
      await directus.request(
        updateItem('circuitos', circuito.id, {
          nombre: values.nombre,
          descripcion: values.descripcion,
          tipo: values.tipo,
          vendedor: values.vendedor,
          tiempo: values.tiempo,
          dia_tiempo: values.tipo === 'tiempo' ? values.dia : null,
          fecha_inicio:
            values.tipo === 'tiempo'
              ? values.fecha_inicio
                ? values.fecha_inicio.format('YYYY-MM-DD')
                : savedDate
              : null,
          dias:
            values.tipo === 'dias'
              ? {
                  create: diasUpdate,
                  update: [],
                  delete: circuito?.dias?.map(d => d.id) || [],
                }
              : {
                  delete: circuito?.dias?.map(d => d.id) || [],
                },
        })
      );

      message.success('Circuito actualizado exitosamente');
      onSuccess?.();
    } catch (err) {
      console.error('Error updating circuit:', err);
      if (err.errors && err.errors[0]?.extensions?.field) {
        setFielderror(err.errors[0].extensions.field);
        setError(true);
        message.error(`Error en el campo: ${err.errors[0].extensions.field}`);
      } else {
        message.error('Error al actualizar el circuito');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTipoChange = e => {
    setTipo(e.target.value);
  };

  return (
    <Spin spinning={loading}>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          tipo: tipo || 'dias',
        }}
      >
        <Form.Item
          name="nombre"
          label="Nombre"
          rules={[
            {
              required: true,
              message: 'Por favor ingrese el nombre del circuito',
            },
          ]}
          validateStatus={fielderror === 'nombre' ? 'error' : ''}
          help={fielderror === 'nombre' ? 'Error en este campo' : ''}
        >
          <Input placeholder="Nombre del circuito" />
        </Form.Item>

        <Form.Item
          name="descripcion"
          label="Descripción"
          rules={[
            { required: true, message: 'Por favor ingrese una descripción' },
          ]}
          validateStatus={fielderror === 'descripcion' ? 'error' : ''}
          help={fielderror === 'descripcion' ? 'Error en este campo' : ''}
        >
          <TextArea rows={4} placeholder="Descripción del circuito" />
        </Form.Item>

        <Form.Item
          name="vendedor"
          label="Vendedor"
          rules={[
            { required: true, message: 'Por favor seleccione un vendedor' },
          ]}
          validateStatus={fielderror === 'vendedor' ? 'error' : ''}
          help={fielderror === 'vendedor' ? 'Error en este campo' : ''}
        >
          <Select
            placeholder="Seleccione un vendedor"
            loading={loadingVendedores}
            showSearch
            optionFilterProp="children"
          >
            {vendedores.map(vendedor => (
              <Select.Option key={vendedor.id} value={vendedor.id}>
                {vendedor.first_name + ' '}
                {vendedor.last_name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="tipo"
          label="Tipo de visita"
          rules={[
            {
              required: true,
              message: 'Por favor seleccione el tipo de visita',
            },
          ]}
        >
          <Radio.Group onChange={handleTipoChange}>
            <Radio value="dias">Por días</Radio>
            <Radio value="tiempo">Por tiempo</Radio>
          </Radio.Group>
        </Form.Item>

        {tipo === 'dias' && (
          <Form.Item
            name="dias"
            label="Días de visita"
            rules={[
              {
                required: tipo === 'dias',
                message: 'Por favor seleccione al menos un día',
              },
            ]}
          >
            <Select
              mode="multiple"
              placeholder="Seleccione los días"
              loading={loading}
              showSearch
              optionFilterProp="children"
            >
              {dias.map(dia => (
                <Select.Option key={dia.id} value={dia.id}>
                  {dia.dia}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}

        {tipo === 'tiempo' && (
          <>
            <Form.Item
              name="tiempo"
              label="Cada cuántos días"
              rules={[
                {
                  required: tipo === 'tiempo',
                  message: 'Por favor ingrese el intervalo de días',
                },
              ]}
            >
              <Input type="number" min={1} placeholder="Ej: 15" />
            </Form.Item>

            <Form.Item
              name="dia_tiempo"
              label="Día"
              rules={[
                {
                  required: tipo === 'tiempo',
                  message: 'Por favor seleccione un día',
                },
              ]}
            >
              <Select
                placeholder="Seleccione el dia"
                loading={loading}
                showSearch
                optionFilterProp="children"
              >
                {dias.map(dia => (
                  <Select.Option key={dia.id} value={dia.id}>
                    {dia.dia}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label={`Fecha de inicio ${moment(savedDate).format(
                'YYYY-MM-DD'
              )}`}
              name="fecha_inicio"
              rules={[
                {
                  type: 'date',
                  message: 'Debe ser una fecha ',
                },
              ]}
            >
              <DatePicker />
            </Form.Item>
          </>
        )}

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            style={{ marginRight: 8 }}
          >
            Guardar cambios
          </Button>
          <Button onClick={onCancel}>Cancelar</Button>
        </Form.Item>
      </Form>
    </Spin>
  );
};

export default EditCircuitoForm;
